/**
 * Gerenciamento de cupons de desconto para o carrinho
 * Implementa aplicação, remoção e validação de códigos de desconto via API do Shopify
 */
(function() {
  'use strict';

  const messages = {
    invalid: 'Código de desconto inválido ou não pode ser combinado com outros descontos.',
    applied: 'Cupom aplicado com sucesso!',
    removed: 'Cupom removido.',
    copied: 'Copiado!',
    enterCode: 'Por favor, digite um código de desconto.',
    errorNetwork: 'Erro de conexão. Verifique sua internet e tente novamente.',
    errorServer: 'Erro no servidor. Tente novamente em alguns instantes.',
    errorTimeout: 'A requisição demorou muito. Tente novamente.',
    errorRemove: 'Erro ao remover cupom.',
    notApplicable: 'Não aplicável a este carrinho.',
    ...window.themeSettings?.cartCouponMessages
  };

  const FETCH_TIMEOUT = 10000; // 10 segundos
  const cartRoot = window.Shopify?.routes?.root || '/';
  let isUpdating = false;
  let couponStateVersion = 0;
  let couponCleanupTask = null;
  let couponCleanupRequested = false;
  async function updateDiscountCodes(codes) {
    const response = await fetchWithTimeout(cartRoot + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: codes.join(',') })
    }, FETCH_TIMEOUT);
    if (!response.ok) {
      throw new Error(response.status >= 500 ? messages.errorServer : messages.invalid);
    }
    return response.json();
  }

  function getCouponCodes(cart, applicableOnly = false) {
    if (Array.isArray(cart.discount_codes)) {
      return [...new Set(cart.discount_codes
        .filter(discount => discount && (!applicableOnly || discount.applicable === true))
        .map(discount => String(discount.code || '').trim().toUpperCase())
        .filter(Boolean))];
    }
    const applications = [
      ...(cart.cart_level_discount_applications || []),
      ...(cart.items || []).flatMap(item =>
        (item.line_level_discount_allocations || []).map(allocation => allocation.discount_application)
      )
    ];
    return [...new Set(applications
      .filter(discount => discount && discount.type === 'discount_code')
      .map(discount => discount.title.toUpperCase()))];
  }

  function hasRejectedCodes(cart) {
    return Array.isArray(cart.discount_codes) && cart.discount_codes.some(discount =>
      discount && discount.applicable === false && discount.code
    );
  }

  function reconcileCouponCodes(cart) {
    renderCouponCodes(cart);
    if (isUpdating || !hasRejectedCodes(cart)) return;
    couponCleanupRequested = true;
    if (couponCleanupTask) return couponCleanupTask;
    couponCleanupTask = cleanRejectedCodes();
    return couponCleanupTask;
  }

  async function cleanRejectedCodes() {
    try {
      let attempts = 0;
      while (couponCleanupRequested && attempts < 2) {
        couponCleanupRequested = false;
        const response = await fetchWithTimeout(cartRoot + 'cart.js');
        if (!response.ok) throw new Error(messages.errorServer);
        const latestCart = await response.json();
        renderCouponCodes(latestCart);
        if (!hasRejectedCodes(latestCart)) continue;
        const cleanedCart = await updateDiscountCodes(getCouponCodes(latestCart, true));
        attempts++;
        renderCouponCodes(cleanedCart);
        if (document.body?.classList?.contains('cart-sidebar-show')) {
          await refreshCart(cleanedCart);
        }
      }
    } catch (error) {
      console.error('Erro ao sincronizar cupons:', error);
    } finally {
      couponCleanupRequested = false;
      couponCleanupTask = null;
    }
  }

  function loadCouponState() {
    if (!document.querySelector('[data-cart-coupons]')) return;
    const initialVersion = couponStateVersion;
    return fetchWithTimeout(cartRoot + 'cart.js').then(response => {
      if (response.ok) return response.json();
    }).then(cart => {
      if (cart && !isUpdating && couponStateVersion === initialVersion) return reconcileCouponCodes(cart);
    }).catch(() => {});
  }

  async function refreshCart(cart) {
    if (typeof window.updateCart === 'function') {
      await window.updateCart(cart);
    }
    updateGlobalCartCount(cart.item_count);
    renderCouponCodes(cart);
  }

  function renderCouponCodes(cart) {
    const container = document.querySelector('[data-cart-coupons]');
    const list = container?.querySelector('.applied-discounts-list');
    if (!list) return;
    couponStateVersion++;

    const codes = getCouponCodes(cart, true);
    list.replaceChildren();
    container.hidden = codes.length === 0;

    codes.forEach(code => {
      const item = document.createElement('li');
      item.className = 'applied-discount-item';
      const label = document.createElement('span');
      label.className = 'discount-code';
      label.textContent = code;
      item.appendChild(label);
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'discount-remove';
      remove.dataset.removeDiscount = code;
      remove.setAttribute('aria-label', container.dataset.removeLabel + ' ' + code);
      remove.textContent = '×';
      item.appendChild(remove);
      list.appendChild(item);
    });
  }

  function init() {
    attachEventListeners();
    loadCouponState();
  }

  function attachEventListeners() {
    // Usar delegação de eventos no documento para funcionar após recargas AJAX
    document.addEventListener('click', handleClick);
    document.addEventListener('keypress', handleKeypress);
    document.addEventListener('cart-update', event => {
      if (event.detail) reconcileCouponCodes(event.detail);
    });
    document.addEventListener('shopify:cart:discount-update', event => {
      if (event?.promise) {
        Promise.resolve(event.promise).then(loadCouponState).catch(() => {});
      } else {
        loadCouponState();
      }
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') loadCouponState();
    });
  }

  function handleClick(e) {
    // Aplicar cupom
    if (e.target.closest('[data-apply-discount]')) {
      e.preventDefault();
      handleApplyDiscount();
      return;
    }

    // Remover cupom
    const removeBtn = e.target.closest('[data-remove-discount]');
    if (removeBtn) {
      e.preventDefault();
      const code = removeBtn.dataset.removeDiscount;
      handleRemoveDiscount(code);
      return;
    }

    // Copiar código promocional
    const copyBtn = e.target.closest('[data-copy-promo]');
    if (copyBtn) {
      e.preventDefault();
      const code = copyBtn.dataset.copyPromo;
      handleCopyPromo(code);
      return;
    }
  }

  function handleKeypress(e) {
    if (e.target.id === 'discount-code-input' && e.key === 'Enter') {
      e.preventDefault();
      handleApplyDiscount();
    }
  }

  async function handleApplyDiscount() {
    if (isUpdating) return;
    const input = document.querySelector('#discount-code-input');
    const messageEl = document.querySelector('.discount-message');
    const applyBtn = document.querySelector('[data-apply-discount]');

    if (!input || !messageEl) return;

    const code = input.value.trim().toUpperCase();

    if (!code) {
      showMessage(messageEl, messages.enterCode, 'error');
      return;
    }

    // Estado de loading
    isUpdating = true;
    setLoadingState(input, applyBtn, true);

    try {
      if (couponCleanupTask) await couponCleanupTask;
      // Buscar cupons já aplicados
      const cartResponse = await fetchWithTimeout(cartRoot + 'cart.js', {}, FETCH_TIMEOUT);

      if (!cartResponse.ok) {
        throw new Error('HTTP ' + cartResponse.status);
      }

      const cart = await cartResponse.json();

      const existingCodes = getCouponCodes(cart, true);

      // Adicionar o novo código se ainda não existe
      if (!existingCodes.includes(code)) {
        existingCodes.push(code);
      }

      // Aplicar todos os códigos juntos
      let updatedCart = await updateDiscountCodes(existingCodes);

      // Verificar se o cupom foi aplicado e é válido
      const appliedDiscount = getCouponCodes(updatedCart, true).includes(code);
      if (getCouponCodes(updatedCart).length !== getCouponCodes(updatedCart, true).length) {
        updatedCart = await updateDiscountCodes(getCouponCodes(updatedCart, true));
      }
      const accepted = appliedDiscount && getCouponCodes(updatedCart, true).includes(code);
      await refreshCart(updatedCart);
      const currentMessage = document.querySelector('.discount-message');

      if (accepted) {
        showMessage(currentMessage, messages.applied, 'success');
        input.value = '';
      } else {
        showMessage(currentMessage, messages.invalid, 'error');
      }

    } catch (error) {
      console.error('Erro ao aplicar desconto:', error);

      let errorMsg = messages.errorServer;
      if (error.name === 'AbortError' || error.message.includes('timeout')) {
        errorMsg = messages.errorTimeout;
      } else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        errorMsg = messages.errorNetwork;
      } else if (error.message === messages.invalid || error.message === messages.errorServer) {
        errorMsg = error.message;
      }

      showMessage(document.querySelector('.discount-message'), errorMsg, 'error');
    } finally {
      setLoadingState(input, applyBtn, false);
      isUpdating = false;
    }
  }

  async function handleRemoveDiscount(code) {
    if (isUpdating) return;
    isUpdating = true;
    const messageEl = document.querySelector('.discount-message');

    try {
      if (couponCleanupTask) await couponCleanupTask;
      const cartResponse = await fetchWithTimeout(cartRoot + 'cart.js', {}, FETCH_TIMEOUT);

      if (!cartResponse.ok) {
        throw new Error('HTTP ' + cartResponse.status);
      }

      const cart = await cartResponse.json();

      const existingCodes = getCouponCodes(cart, true)
        .filter(c => c !== code.toUpperCase());

      const discountString = existingCodes.join(',');

      const response = await fetchWithTimeout(cartRoot + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discount: discountString || ''
        })
      }, FETCH_TIMEOUT);

      if (!response.ok) {
        const errorMsg = response.status >= 500 ? messages.errorServer : messages.errorRemove;
        throw new Error(errorMsg);
      }

      const updatedCart = await response.json();

      await refreshCart(updatedCart);
      showMessage(document.querySelector('.discount-message'), messages.removed, 'success');

    } catch (error) {
      console.error('Erro ao remover desconto:', error);

      if (messageEl) {
        let errorMsg = messages.errorServer;
        if (error.name === 'AbortError' || error.message.includes('timeout')) {
          errorMsg = messages.errorTimeout;
        } else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
          errorMsg = messages.errorNetwork;
        } else if (error.message === messages.errorRemove) {
          errorMsg = error.message;
        }
        showMessage(document.querySelector('.discount-message'), errorMsg, 'error');
      }
    } finally {
      isUpdating = false;
    }
  }

  function handleCopyPromo(code) {
    if (!navigator.clipboard) {
      // Fallback para navegadores mais antigos
      const tempInput = document.createElement('input');
      tempInput.value = code;
      tempInput.style.position = 'absolute';
      tempInput.style.left = '-9999px';
      document.body.appendChild(tempInput);
      tempInput.select();
      document.execCommand('copy');
      document.body.removeChild(tempInput);

      showCopyFeedback();
      return;
    }

    navigator.clipboard.writeText(code).then(() => {
      showCopyFeedback();
    }).catch(err => {
      console.error('Erro ao copiar:', err);
    });
  }

  function showCopyFeedback() {
    const feedback = document.querySelector('.promo-copy-feedback');
    if (feedback) {
      feedback.textContent = messages.copied;
      feedback.classList.add('show');
      setTimeout(() => {
        feedback.classList.remove('show');
        feedback.textContent = '';
      }, 2000);
    }
  }

  function showMessage(element, message, type) {
    if (!element) return;

    element.textContent = message;
    element.className = 'discount-message ' + type;

    setTimeout(() => {
      element.textContent = '';
      element.className = 'discount-message';
    }, 5000);
  }

  function updateGlobalCartCount(count) {
    const counters = document.querySelectorAll('[data-cart-count]');
    counters.forEach(counter => {
      counter.textContent = count;
    });

    const textElements = document.querySelectorAll('[data-cart-text]');
    textElements.forEach(el => {
      el.textContent = count === 1 ? 'item' : 'itens';
    });
  }

  function setLoadingState(input, button, isLoading) {
    if (input) {
      input.disabled = isLoading;
    }
    if (button) {
      button.disabled = isLoading;
      button.classList.toggle('is-loading', isLoading);
      if (isLoading) {
        button.dataset.idleLabel = button.textContent;
      }
      button.textContent = isLoading
        ? (window.themeSettings?.cartCouponLoadingLabel || 'Aplicando...')
        : (button.dataset.idleLabel || 'Aplicar');
    }
  }

  async function fetchWithTimeout(url, options = {}, timeout = 10000) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        const timeoutError = new Error('Request timeout');
        timeoutError.name = 'AbortError';
        throw timeoutError;
      }
      throw error;
    }
  }

  // Inicializar quando o DOM estiver pronto
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
